import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Factory,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  ClipboardList,
  Star,
  Target,
  BarChart3,
  Building2,
  Leaf,
} from "lucide-react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const BRCGS_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <ShieldCheck size={18} className="text-orange-400" />
            <span>Food Safety Certification</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-orange-400 via-red-400 to-pink-400">
            BRCGS Certification
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Brand Reputation through Compliance Global Standards - Ensuring Food
            Safety, Quality, and Regulatory Compliance Across the Supply Chain
          </p>
        </motion.div>
      </section>

      {/* What is BRCGS */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is BRCGS Certification?
          </h2>
          <div className="space-y-6 text-lg text-gray-700">
            <p>
              BRCGS certification confirms that a food or packaging business has
              implemented an effective Food Safety and Quality Management System
              aligned with internationally recognized best practices. The
              certification focuses on ensuring products are consistently safe,
              legal, and of high quality, while meeting customer, regulatory,
              and international market requirements.
            </p>
            <p>
              Originally developed by leading UK retailers, BRCGS standards are
              now accepted worldwide by retailers, brand owners, and food
              service companies as a benchmark for supplier approval. BRCGS is
              often a mandatory requirement for supplying to major retailers,
              supermarkets, and international buyers.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Key Features of BRCGS Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Food Safety Assurance",
                desc: "Ensures products are consistently safe, legal, and meet defined quality standards.",
                icon: ShieldCheck,
              },
              {
                title: "Risk-Based Approach",
                desc: "Identifies, evaluates, and controls food safety and quality risks across processes.",
                icon: Target,
              },
              {
                title: "Regulatory Compliance",
                desc: "Aligns operations with national and international food safety regulations.",
                icon: CheckCircle,
              },
              {
                title: "Retailer Acceptance",
                desc: "Recognized and trusted by global retailers, brand owners, and food service chains.",
                icon: Award,
              },
              {
                title: "Traceability & Transparency",
                desc: "Strengthens traceability, recall readiness, and supplier approval systems.",
                icon: FileCheck,
              },
              {
                title: "Supplier Credibility",
                desc: "Enhances credibility and approval status in global supply chains.",
                icon: TrendingUp,
              },
            ].map((feature, i) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                >
                  <IconComponent className="w-10 h-10 text-orange-500 mb-4" />
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-700">{feature.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* Food Safety & Quality Management System */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is a Food Safety & Quality Management System?
          </h2>
          <div className="bg-gradient-to-r from-orange-50 to-red-50 p-8 rounded-lg">
            <p className="text-lg text-gray-700 mb-6">
              A Food Safety and Quality Management System under BRCGS is a
              structured framework that integrates:
            </p>
            <ul className="space-y-3">
              {[
                "HACCP-based food safety plans",
                "Documented procedures and controls",
                "Monitoring, verification, and validation activities",
                "Internal audits and corrective actions",
                "Management commitment and continual improvement",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-lg text-gray-700 mt-6">
              This system ensures risks are controlled proactively rather than
              reactively.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Importance */}
      <section className="py-16 px-4 bg-gray-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Importance of BRCGS Certification for Businesses
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Ensures compliance with retailer and customer requirements",
              "Improves food safety culture and operational discipline",
              "Reduces product recalls, complaints, and non-conformities",
              "Strengthens customer confidence and brand reputation",
              "Facilitates access to international and high-value markets",
              "Enhances supplier credibility and approval status",
            ].map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-4"
              >
                <Star className="w-6 h-6 text-yellow-500 mt-1 flex-shrink-0" />
                <span className="text-gray-700 text-lg">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Significance */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Significance of BRCGS Certification
          </h2>
          <p className="text-lg text-gray-700 mb-6">
            BRCGS helps organizations:
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              "Demonstrate strong food safety governance",
              "Meet retailer and export market expectations",
              "Improve consistency in product quality",
              "Strengthen risk management and traceability",
              "Enhance internal controls and accountability",
              "Support continual improvement through audit findings",
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                className="bg-gradient-to-br from-orange-100 to-red-100 p-6 rounded-lg"
              >
                <CheckCircle className="w-6 h-6 text-orange-600 mb-3" />
                <p className="text-gray-800 font-medium">{item}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Industries */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Industries That Require BRCGS Certification
          </h2>
          <p className="text-lg text-gray-700 mb-8">
            BRCGS certification is widely required in the following sectors:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Food Manufacturing",
              "Food Processing",
              "Food Packaging",
              "Storage & Distribution",
              "Cold Storage & Warehousing",
              "Export-Oriented Food Businesses",
              "Private Label & Retail Supply Chains",
              "Ready-to-eat Food Production",
            ].map((industry, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 p-4 bg-white rounded-lg"
              >
                <Factory className="w-5 h-5 text-orange-500 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{industry}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* How to Get Certification */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            How to Get BRCGS Certification
          </h2>
          <div className="space-y-6">
            {[
              {
                step: 1,
                title: "Gap Analysis",
                desc: "Assess your current practices against BRCGS standard requirements",
              },
              {
                step: 2,
                title: "System Development",
                desc: "Develop and implement food safety systems and quality controls",
              },
              {
                step: 3,
                title: "HACCP Study",
                desc: "Conduct HACCP studies and perform comprehensive risk assessments",
              },
              {
                step: 4,
                title: "Documentation",
                desc: "Document all policies, procedures, and records comprehensively",
              },
              {
                step: 5,
                title: "Staff Training",
                desc: "Provide training and enhance food safety awareness across the organization",
              },
              {
                step: 6,
                title: "Internal Audits",
                desc: "Conduct internal audits and implement corrective actions",
              },
              {
                step: 7,
                title: "Certification Audit",
                desc: "Undergo certification audit by an accredited certification body",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.08 }}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                  {item.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-r from-orange-50 via-red-50 to-pink-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900 text-center">
            Why Choose MBA Solutions for BRCGS Certification?
          </h2>
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <p className="text-lg text-gray-700 mb-6">
              MBA Solutions Pvt. Ltd. is a trusted BRCGS consultancy firm
              providing complete implementation, training, and audit preparation
              services. We support food and packaging businesses with:
            </p>
            <ul className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "Practical, site-specific documentation",
                "HACCP development and validation",
                "Internal audit and mock audit support",
                "Certification body coordination",
                "Audit closure and continual improvement guidance",
                "Expert BRCGS consultant support",
              ].map((service, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{service}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 mb-6">
              Our consultants have hands-on experience with multiple BRCGS
              certification bodies, ensuring a smooth, compliant, and successful
              certification journey.
            </p>
            <div className="border-t pt-6 mt-6">
              <p className="text-gray-800 font-semibold mb-4">Get in Touch:</p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-orange-500" />
                  <span className="text-gray-700">
                    <strong>Email:</strong> atif@mbasolutions.co
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-orange-500" />
                  <span className="text-gray-700">
                    <strong>Website:</strong> www.mbasolutions.co
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </>
  );
};

export default BRCGS_Details;
